import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notification } from 'antd';
import { debounce } from 'lodash';
import APIClient from '../api/api';
import { LanderSortBy, LanderStats, ListLandersResponse } from '../api/models';
import { Callout, Column, DataTable, DomainName, Num, Pagination } from '../components/ui';
import { DateRangePopover, useDateRangeFilter } from '../components/DateRangeFilter';
import { useSlowRequest } from '../hooks/useSlowRequest';
import { PageFooter, PageHead, PageToolbar } from '../layout';

const DEFAULT_PAGE_SIZE = 50;

// Searching a lander the nightly sync does not store falls through to a live
// Plausible lookup, so a keystroke is expensive enough to be worth waiting on.
const SEARCH_DEBOUNCE_MS = 500;

// looksLikeDomain mirrors what the server will answer live: a single hostname,
// never a fragment, since a fragment would need a scan across every lander.
export function looksLikeDomain(search: string): boolean {
  const trimmed = search.trim();
  if (!trimmed || /[\s%_*/?]/.test(trimmed)) return false;
  return trimmed.includes('.') && !trimmed.endsWith('.');
}

const Landers = () => {
  const navigate = useNavigate();
  const { dateRange, setDateRange, selectedQuickRange, setSelectedQuickRange } = useDateRangeFilter('this-month');

  const [landers, setLanders] = useState<ListLandersResponse>();
  const [loading, setLoading] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState<LanderSortBy>('visitors');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadLanders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, search, page, pageSize, sortBy, sortDir]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setPage(1);
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // A search naming one hostname is the case the server can answer live, so it
  // is the only one that can outrun the stored set and take its time doing it.
  const searchingLive = useSlowRequest(loading) && looksLikeDomain(search);

  async function loadLanders() {
    setLoading(true);
    try {
      const resp = await APIClient.listLanders(
        { page, pageSize },
        {
          startDate: dateRange[0].toDate(),
          endDate: dateRange[1].toDate(),
          search: search || undefined,
          sortBy,
          sortDir,
        },
      );
      setLanders(resp);
    } catch (e) {
      notification.error({ message: 'Failed to load landers.' });
    } finally {
      setLoading(false);
    }
  }

  function toggleSort(key: LanderSortBy) {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir(key === 'domain' ? 'asc' : 'desc');
    }
    setPage(1);
  }

  function sortProps(key: LanderSortBy) {
    return {
      sortable: true,
      sortDirection: sortBy === key ? sortDir : undefined,
      onSort: () => toggleSort(key),
    };
  }

  const columns: Column<LanderStats>[] = [
    {
      key: 'domain',
      title: 'Lander',
      ...sortProps('domain'),
      render: (l) => <DomainName name={l.domain} clickable={false} />,
    },
    {
      key: 'visitors',
      title: 'Uniques',
      align: 'right',
      width: 120,
      ...sortProps('visitors'),
      render: (l) => <Num value={l.visitors} />,
    },
    {
      key: 'visits',
      title: 'Visits',
      align: 'right',
      width: 120,
      ...sortProps('visits'),
      render: (l) => <Num value={l.visits} />,
    },
    {
      key: 'pageviews',
      title: 'Pageviews',
      align: 'right',
      width: 120,
      ...sortProps('pageviews'),
      render: (l) => <Num value={l.pageviews} />,
    },
    {
      key: 'events',
      title: 'Events',
      align: 'right',
      width: 120,
      render: (l) => <Num value={l.events} />,
    },
  ];

  return (
    <>
      <PageHead
        title="Landers"
        subtitle="Traffic per lander over the selected date range."
        actions={
          <DateRangePopover
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedQuickRange={selectedQuickRange}
            setSelectedQuickRange={setSelectedQuickRange}
            loading={loading}
          />
        }
      />

      <PageToolbar>
        <input
          type="text"
          value={searchDraft}
          placeholder="Search landers…"
          onChange={(e) => {
            setSearchDraft(e.target.value);
            debouncedSearch(e.target.value);
          }}
          className="bg-bg-elevated border border-transparent rounded-md px-3 py-1 w-[240px] text-sm text-text outline-none focus:border-border-strong placeholder:text-text-3"
        />
        {searchDraft && (
          <button
            type="button"
            onClick={() => {
              debouncedSearch.cancel();
              setSearchDraft('');
              setSearch('');
              setPage(1);
            }}
            className="text-2xs text-text-3 hover:text-text font-mono px-1"
          >
            clear
          </button>
        )}

        <span className="ml-auto text-2xs text-text-3 font-mono">
          {landers ? (
            <>
              total <b className="text-text-2">{landers.total.toLocaleString()}</b>
            </>
          ) : (
            'loading…'
          )}
        </span>
      </PageToolbar>

      {searchingLive && (
        <div className="mb-2.5">
          <Callout tone="info" title="Looking this lander up live">
            Only the busiest landers are stored each day. {search} is not among them for this period,
            so its traffic is being read from Plausible directly. That takes around ten seconds.
          </Callout>
        </div>
      )}

      <DataTable
        columns={columns}
        data={landers?.content ?? []}
        rowKey={(l) => l.domain}
        loading={loading}
        emptyMessage={
          search
            ? 'No lander matching that search had traffic in this period.'
            : 'No landers with traffic in this period.'
        }
        rowHref={(l) => `/landers/${encodeURIComponent(l.domain)}`}
        onRowClick={(l) => navigate(`/landers/${encodeURIComponent(l.domain)}`)}
      />
      <PageFooter
        left={
          <span>
            Showing <b className="text-text-2">{landers?.content.length ?? 0}</b> of{' '}
            <b className="text-text-2">{landers?.total.toLocaleString() ?? 0}</b>
          </span>
        }
        right={
          <Pagination
            page={page}
            pageSize={pageSize}
            total={landers?.total ?? 0}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            loading={loading}
          />
        }
      />
    </>
  );
};

export default Landers;
